import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './ui/button';

interface State {
  err: Error | null;
}

interface Props {
  children: ReactNode;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', err, info.componentStack);
  }

  reset = (): void => this.setState({ err: null });

  render(): ReactNode {
    if (this.state.err) {
      return (
        <div className="flex min-h-full items-center justify-center bg-surface-bg p-6">
          <div className="w-full max-w-md rounded-xl border border-state-danger bg-surface-raised p-6">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-state-danger">
              Something broke
            </div>
            <h1 className="mb-3 text-xl font-bold">
              The dashboard hit an unexpected error.
            </h1>
            <p className="mb-4 text-sm text-fg-muted">
              The data is fine — this is a client-side rendering issue. Reload
              the page, or click below to try again.
            </p>
            <pre className="mb-4 max-h-40 overflow-auto rounded-md bg-surface-bg p-3 text-xs text-fg-subtle">
              {this.state.err.message}
            </pre>
            <div className="flex gap-3">
              <Button onClick={this.reset}>Try again</Button>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                Reload page
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
